/**
 * useExposureProgression — calm fetcher + writer for the
 * Progressive Environmental Exposure surface.
 *
 * Stage-2 P5 (Feb 18, 2026). Read-only by default; write actions
 * (log, state patch) are explicit instructor calls.
 */
import { useCallback, useEffect, useState } from "react";
import axios from "axios";

const API_URL = process.env.REACT_APP_BACKEND_URL;


const EMPTY_SUMMARY = Object.freeze({
  groups: [
    { key: "often",  label: "Often experienced",   items: [], count: 0 },
    { key: "mid",    label: "Some experience",     items: [], count: 0 },
    { key: "rarely", label: "Rarely experienced",  items: [], count: 0 },
    { key: "never",  label: "Not yet experienced", items: [], count: 0 },
  ],
});

const EMPTY_VOCAB = Object.freeze({
  categories: [], stability_trends: [], confidence_range: { min: 1, max: 5 },
});


export default function useExposureProgression({ learnerId, enabled = true } = {}) {
  const [vocab, setVocab] = useState(EMPTY_VOCAB);
  const [summary, setSummary] = useState(EMPTY_SUMMARY);
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);

  const fetchVocab = useCallback(async () => {
    try {
      const r = await axios.get(
        `${API_URL}/api/exposure-progression/vocabulary`,
        { withCredentials: true },
      );
      setVocab(r.data);
    } catch { /* silent */ }
  }, []);

  const fetchBreadth = useCallback(async () => {
    if (!enabled || !learnerId) return;
    setLoading(true);
    try {
      const r = await axios.get(
        `${API_URL}/api/exposure-progression/learner/${learnerId}`,
        { withCredentials: true },
      );
      setSummary(r.data?.summary || EMPTY_SUMMARY);
      setRows(r.data?.rows || []);
    } catch {
      setSummary(EMPTY_SUMMARY);
      setRows([]);
    } finally {
      setLoading(false);
    }
  }, [enabled, learnerId]);

  useEffect(() => { fetchVocab(); }, [fetchVocab]);
  useEffect(() => { fetchBreadth(); }, [fetchBreadth]);

  const logExposure = useCallback(async ({
    category, complexity, movingState,
    confidenceLevel, stabilityTrend, lessonId, note,
  }) => {
    if (!learnerId) return { ok: false };
    try {
      const r = await axios.post(
        `${API_URL}/api/exposure-progression/log`,
        {
          learner_id: learnerId,
          category, complexity: complexity || null,
          confidence_level: confidenceLevel || null,
          stability_trend:  stabilityTrend || null,
          lesson_id: lessonId || null,
          note: note || null,
          moving_state: movingState || "unknown",
        },
        { withCredentials: true },
      );
      await fetchBreadth();
      return { ok: true, ...r.data };
    } catch (err) {
      const d = err?.response?.data?.detail;
      return {
        ok: false,
        code: d?.code || "error",
        message: d?.message || "Could not log exposure.",
      };
    }
  }, [learnerId, fetchBreadth]);

  const updateState = useCallback(async (stateId, patch) => {
    try {
      const r = await axios.patch(
        `${API_URL}/api/exposure-progression/state/${stateId}`,
        patch,
        { withCredentials: true },
      );
      await fetchBreadth();
      return { ok: true, ...r.data };
    } catch (err) {
      const d = err?.response?.data?.detail;
      return {
        ok: false,
        code: d?.code || "error",
        message: d?.message || "Could not update.",
      };
    }
  }, [fetchBreadth]);

  return { vocab, summary, rows, loading, logExposure, updateState, refresh: fetchBreadth };
}
